import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function Billing() {
  const [plans, setPlans] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingPlan, setPayingPlan] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/subscription/plans"),
      api.get("/subscription"),
    ])
      .then(([plansRes, subscriptionRes]) => {
        setPlans(plansRes.data || {});
        setSubscription(subscriptionRes.data || null);
      })
      .catch((err) => {
        console.error(err);
        setError(
          err?.response?.data?.detail ||
            "No se pudieron cargar los planes."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const choosePlan = async (plan) => {
    try {
      setError("");
      setPayingPlan(plan);

      const res = await api.post("/subscription/checkout", {
        plan,
      });

      if (!res.data?.checkout_url) {
        throw new Error("Stripe no devolvió una URL de pago.");
      }

      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error(err);

      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "No se pudo iniciar el pago. Inténtalo de nuevo."
      );

      setPayingPlan(null);
    }
  };

  const manageSubscription = async () => {
    try {
      setError("");
      setOpeningPortal(true);

      const res = await api.post("/subscription/portal");

      if (!res.data?.portal_url) {
        throw new Error("Stripe no devolvió la URL del portal.");
      }

      window.location.href = res.data.portal_url;
    } catch (err) {
      console.error(err);

      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "No se pudo abrir la gestión de la suscripción."
      );

      setOpeningPortal(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <p>Cargando planes...</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">
          Planes y facturación
        </h1>

        {subscription && (
          <div className="mt-2">
            <p className="text-gray-500">
              Plan actual:{" "}
              <strong>{subscription.plan_name}</strong>

              {subscription.status === "trialing" &&
                subscription.days_remaining !== null &&
                ` · ${subscription.days_remaining} días de prueba restantes`}
            </p>

            {subscription.stripe_customer_id && (
              <button
                type="button"
                onClick={manageSubscription}
                disabled={openingPortal}
                className="mt-4 rounded-xl border border-gray-300 bg-white px-4 py-2 font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {openingPortal
                  ? "Abriendo..."
                  : "Gestionar suscripción"}
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {Object.entries(plans).map(([key, plan]) => {
          const isCurrentPlan = subscription?.plan === key;
          const isPaying = payingPlan === key;

          return (
            <div
              key={key}
              className="mantix-card flex flex-col p-6"
            >
              <h2 className="text-2xl font-semibold">
                {plan.name}
              </h2>

              <p className="mt-2 text-gray-500">
                {plan.description}
              </p>

              <div className="mt-6">
                <span className="text-4xl font-bold">
                  {plan.price} €
                </span>

                <span className="text-gray-500">
                  {" "}
                  / mes
                </span>
              </div>

              <p className="mt-3 text-sm text-gray-500">
                Hasta {plan.technicians_limit} técnicos
              </p>

              <ul className="mt-6 flex-1 space-y-3">
                {(plan.features || []).map((feature) => (
                  <li
                    key={feature}
                    className="flex gap-2"
                  >
                    <span>✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => choosePlan(key)}
                disabled={isCurrentPlan || isPaying}
                className="mt-8 rounded-xl bg-[#0066CC] px-4 py-3 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCurrentPlan
                  ? "Plan actual"
                  : isPaying
                  ? "Abriendo pago..."
                  : "Elegir plan"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mantix-card mt-8 p-6">
        <h2 className="text-xl font-semibold">
          Prueba gratuita
        </h2>

        <p className="mt-2 text-gray-500">
          14 días gratis, sin permanencia y con configuración inicial incluida.
        </p>
      </div>
    </div>
  );
}